import type { History, Location } from 'history'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppContext } from '@/app-context'
import DialogChangeInstitution from '@/components/molecules/dialog-change-institution'
import {
  AngleDown as IconAngleDown,
  Repeat as IconRepeat,
  Cross as IconCross
} from '@/components/ui/icons'
import { privatePages, sidebar } from '@/config/menu'

type SidebarChildEntry = {
  Icon?: React.ComponentType<{ className?: string }> | string
  IconActive?: React.ComponentType<{ className?: string }> | string
  pathname: string
  title: string
  type?: 'icon' | 'image'
}

type SidebarEntry = {
  Icon?: React.ComponentType<{ className?: string }> | string
  IconActive?: React.ComponentType<{ className?: string }> | string
  pathname?: string
  title: string
  type?: 'icon' | 'image'
  children?: SidebarChildEntry[]
}

type PrivatePage = {
  path: string
  menu: string
  name: string
}

type SidebarProps = {
  history: History
  location: Location
  isOpen?: boolean
  onClose?: () => void
}

const isComponent = (
  icon?: SidebarEntry['Icon'] | SidebarChildEntry['Icon']
): icon is React.ComponentType<{ className?: string }> =>
  typeof icon === 'function'

const SideBar = ({
  history,
  location,
  isOpen = false,
  onClose
}: SidebarProps) => {
  const { isGold, hasMultipleInstitutions, currentInstitution } =
    useAppContext()
  const [activeMenu, setActiveMenu] = useState<string>(location.pathname)
  const [sidebarMenu, setSidebarMenu] = useState<SidebarEntry[]>([])
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    {}
  )
  const [isChangeInstitutionOpen, setIsChangeInstitutionOpen] = useState(false)

  const sidebarItems = sidebar as SidebarEntry[]

  const handleNavigate = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
      pathname?: string
    ) => {
      event.preventDefault()

      if (!pathname || pathname === location.pathname) {
        return
      }

      setActiveMenu(pathname)
      history.push(pathname)
      // Close sidebar on mobile after navigation
      if (onClose) {
        onClose()
      }
    },
    [history, location.pathname, onClose]
  )

  const handleToggleParent = useCallback((title: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [title]: !(prev[title] ?? false)
    }))
  }, [])

  const handleInitializeSidebar = useCallback(async () => {
    const filteredMenus = isGold
      ? sidebarItems
      : sidebarItems.filter((item) => item.title !== 'Portofolio Gold')

    setSidebarMenu(filteredMenus)
  }, [isGold, sidebarItems])

  useEffect(() => {
    const currentPath = location.pathname
    const allPages = Object.values(privatePages) as PrivatePage[]
    const exactMatch = allPages.find((page) => page.path === currentPath)

    if (exactMatch?.menu) {
      setActiveMenu(exactMatch.menu)
      return
    }

    const isProductMutualFund = currentPath.includes('/product/mutualfund')
    const isProductGold = currentPath.includes('/product/gold')
    const isProduct = currentPath.includes('/product')
    const isTransaction = currentPath.includes('/step_1')

    if (isProductMutualFund) {
      setActiveMenu(`${process.env.REACT_APP_PREFIX}/product/mutualfund`)
      return
    }

    if (isProductGold) {
      setActiveMenu(`${process.env.REACT_APP_PREFIX}/product/gold`)
      return
    }

    if (isProduct) {
      setActiveMenu(`${process.env.REACT_APP_PREFIX}/product`)
      return
    }

    if (isTransaction) {
      const transactionPage = allPages.find(
        (page) => page.name === 'transaction'
      )
      if (transactionPage?.menu) {
        setActiveMenu(transactionPage.menu)
        return
      }
    }

    const partialMatch = allPages.find((page) =>
      currentPath.includes(page.path)
    )

    if (partialMatch?.menu) {
      setActiveMenu(partialMatch.menu)
      return
    }

    setActiveMenu(currentPath)
  }, [location.pathname])

  useEffect(() => {
    handleInitializeSidebar()
  }, [handleInitializeSidebar])

  useEffect(() => {
    setExpandedMenus((prev) => {
      const next = { ...prev }
      sidebarMenu.forEach((item) => {
        if (item.children?.some((child) => child.pathname === activeMenu)) {
          next[item.title] = true
        }
      })
      return next
    })
  }, [activeMenu, sidebarMenu])

  const profileData = useMemo(
    () => ({
      name: currentInstitution?.name ?? '',
      investor_name: currentInstitution?.investor_name ?? '',
      institution_name: currentInstitution?.institution_name ?? '',
      sid: currentInstitution?.sid ?? ''
    }),
    [currentInstitution]
  )

  const renderIcon = (item: SidebarEntry | SidebarChildEntry) => {
    if (item.type === 'icon' && isComponent(item.Icon)) {
      const IconComponent = item.Icon
      return <IconComponent />
    }

    const iconSrc = item.Icon as string | undefined

    if (iconSrc) {
      return (
        <img
          src={iconSrc}
          alt=""
          className="ml-[2px] h-6 w-6"
          aria-hidden="true"
        />
      )
    }

    return null
  }

  return (
    <aside
      className={`w-[256px] space-y-2 py-6 px-3 bg-flat-white absolute top-0 left-0 h-screen z-[999] transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}
      style={{
        boxShadow:
          '0px 6px 16px 0px rgba(61, 71, 81, 0.06), 0px 0px 4px 1px rgba(61, 71, 81, 0.04)'
      }}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="md:hidden absolute top-4 right-4 p-2 text-neutral-gray-600 hover:text-neutral-gray-800"
          aria-label="Close sidebar"
          data-test-id="layout_sidebar_button_close"
        >
          <IconCross className="size-6" />
        </button>
      )}
      <div className="text-center">
        <img
          className="w-[112px] mx-auto cursor-pointer"
          alt="Logo Bareksa"
          title="Logo Bareksa"
          src="/bareksa-partner-of-grab.png"
          data-test-id="layout_sidebar_link_home"
          onClick={() => {
            history.push('/')
            if (onClose) {
              onClose()
            }
          }}
        />
      </div>

      <div className="py-10 space-y-1">
        <p className="subheading-sm-regular text-neutral-gray-800">
          Selamat Datang,
          <br />
          {profileData.name}
        </p>
        <p className="body-2xs-regular text-neutral-gray-600">
          {profileData.institution_name || '-'}
        </p>
        <p className="body-2xs-regular text-neutral-gray-600">
          Info SID - <span className="body-2xs-bold">{profileData.sid}</span>
        </p>
        {hasMultipleInstitutions && (
          <p
            className="flex w-fit cursor-pointer items-center gap-1 body-2xs-regular text-[rgba(139,62,170,1)]"
            onClick={() => setIsChangeInstitutionOpen(true)}
            data-test-id="layout_sidebar_button_switch_institution"
          >
            <IconRepeat className="size-4" />
            <span>Ganti Institusi</span>
          </p>
        )}
      </div>

      <ul className="flex flex-col gap-2 list-none list-inside [&>li>:first-child]:!ml-0">
        {sidebarMenu.map((menuItem) => {
          const children = menuItem.children ?? []
          const hasChildren = children.length > 0
          const childActive = hasChildren
            ? children.some((child) => child.pathname === activeMenu)
            : false
          const isActive =
            (!hasChildren && menuItem.pathname === activeMenu) || childActive
          const isExpanded = hasChildren
            ? (expandedMenus[menuItem.title] ?? childActive)
            : false

          return (
            <li key={menuItem.pathname ?? menuItem.title} className="last:mb-0">
              {hasChildren ? (
                <div
                  className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-1 py-3 transition-colors duration-150 hover:text-biome-green-500 ${childActive ? 'text-biome-green-500' : 'text-neutral-gray-300'}`}
                  onClick={() => handleToggleParent(menuItem.title)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleToggleParent(menuItem.title)
                    }
                  }}
                  aria-expanded={isExpanded}
                  data-test-id={`layout_sidebar_button_menu_${menuItem.title.toLowerCase().replace(/\s+/g, '_')}`}
                >
                  <span className="flex h-6 w-6 items-center justify-center">
                    {renderIcon(menuItem)}
                  </span>
                  <p className={`subheading-xs-regular `}>{menuItem.title}</p>
                  <IconAngleDown
                    className={`shrink-0 ml-auto size-6 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              ) : (
                <a
                  href={menuItem.pathname}
                  onClick={(event) => handleNavigate(event, menuItem.pathname)}
                  className={`flex w-full items-center gap-3 rounded-lg px-1 py-3 transition-colors duration-150 no-underline hover:text-biome-green-500 ${
                    isActive
                      ? 'bg-biome-green-50 text-biome-green-500'
                      : 'text-neutral-gray-300'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                  data-test-id={`layout_sidebar_link_menu_${menuItem.title.toLowerCase().replace(/\s+/g, '_')}`}
                >
                  <span className="flex h-6 w-6 items-center justify-center">
                    {renderIcon(menuItem)}
                  </span>
                  <p className={`subheading-xs-regular `}>{menuItem.title}</p>
                </a>
              )}

              {hasChildren && isExpanded && (
                <ul className="mt-2 space-y-1 list-none">
                  {children.map((child) => {
                    const childIsActive = activeMenu === child.pathname
                    return (
                      <li key={child.pathname}>
                        <a
                          href={child.pathname}
                          onClick={(event) =>
                            handleNavigate(event, child.pathname)
                          }
                          className={`flex items-center gap-3 rounded-lg px-1 py-3 transition-colors duration-150 no-underline pl-6 hover:text-biome-green-500 ${
                            childIsActive
                              ? 'bg-biome-green-50 text-biome-green-500'
                              : 'text-neutral-gray-300'
                          }`}
                          aria-current={childIsActive ? 'page' : undefined}
                          data-test-id={`layout_sidebar_link_menu_${child.title.toLowerCase().replace(/\s+/g, '_')}`}
                        >
                          <span className="flex h-5 w-5 items-center justify-center">
                            {renderIcon(child)}
                          </span>
                          <p className={`subheading-xs-regular `}>
                            {child.title}
                          </p>
                        </a>
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

      <DialogChangeInstitution
        isOpen={isChangeInstitutionOpen}
        onClose={() => setIsChangeInstitutionOpen(false)}
        history={history}
      />
    </aside>
  )
}

export default SideBar
